<template>
  <div class="widget-action-card" :class="config?.theme?.components?.card">
    <div class="widget-action-card_header" :class="componentStyles?.title || ''">
      <template v-if="title">
        <div class="flex items-center justify-start gap-3">
          <WidgetIcon v-if="icon" :icon="icon" class="widget-primary-icon" height="2rem" />
          <span>{{ title }}</span>
        </div>
      </template>
      <slot v-else name="title"></slot>
    </div>
    <div class="widget-action-card_body" :class="componentStyles?.body || ''">
      <template v-if="content">
        {{ content }}
      </template>
      <slot v-else name="content"></slot>
    </div>
    <div class="widget-action-card_action">
      <template v-if="action">
        <WButton
          type="ghost"
          :class="twMerge(cardButtonVariants({ variant }), String($attrs.class || ''))"
          :disabled="disabled"
          @click="emit('action')"
        >
          {{ action }}
        </WButton>
      </template>
      <template v-else-if="actions">
        <div class="flex items-center justify-start gap-2">
          <WButton
            v-for="act in actions"
            :key="act"
            type="ghost"
            :class="twMerge(cardButtonVariants({ variant }), String($attrs.class || ''))"
            :disabled="disabled"
            @click="emit('action', action)"
          >
            {{ action }}
          </WButton>
        </div>
      </template>
      <slot v-else name="action"></slot>
    </div>
  </div>
</template>

<script setup lang="ts">
  import WidgetIcon from './WidgetIcon.vue';
  import WButton from 'src/components/base/Button.vue';
  import { cva, type VariantProps } from 'class-variance-authority';
  import { twMerge } from 'tailwind-merge';
  import { config, getClientTheme } from 'src/utils/config';
  import { computed } from 'vue';

  type CardProps = VariantProps<typeof cardButtonVariants>;

  defineProps<{
    icon?: string;
    content?: string;
    title?: string;
    action?: string;
    actions?: string[];
    disabled?: boolean;
    variant?: CardProps['variant'];
  }>();

  const emit = defineEmits<{
    (event: 'action', action?: string): void;
  }>();
  const componentStyles = computed(() => config.value.theme?.components);

  const cardButtonVariants = cva('', {
    variants: {
      variant: {
        default: 'action1-bold text-primary hover:text-primary-400 hover:bg-transparent underline underline-offset-2',
        audi: 'body1 text-primary p-0 hover:text-primary-400 hover:bg-transparent underline underline-offset-2',
        tesla: 'body1 text-primary p-0 hover:text-primary-400 hover:bg-transparent underline underline-offset-2',
        aa: 'body1 text-primary p-0 hover:text-primary-400 hover:bg-transparent underline underline-offset-2',
        suzuki: 'body1 text-primary p-0 hover:text-primary-400 hover:bg-transparent underline underline-offset-2',
        cinch: 'body1 text-primary p-0 hover:text-primary-400 hover:bg-transparent underline underline-offset-2',
        volkswagen: 'body1 text-primary hover:text-primary-400 hover:bg-transparent underline underline-offset-2',
        bentley: 'body1 text-primary hover:text-primary-400 hover:bg-transparent underline underline-offset-2',
        vwg: 'body1 text-primary hover:text-primary-400 hover:bg-transparent underline underline-offset-2',
        samsung: 'body1 text-primary hover:text-primary-400 hover:bg-transparent underline underline-offset-2',
        mercedes: 'action1-bold text-primary hover:text-primary-400 hover:bg-transparent',
        bmw: 'body1-bold p-0 border-b border-fontPrimary hover:text-primary hover:border-primary disabled:opacity-35 disabled:cursor-not-allowed disabled:hover:text-fontPrimary disabled:hover:border-fontPrimary focus:text-primary focus:border-primary',
        toyota:
          'body1-bold py-3 px-8 bg-primary text-white border-primary hover:bg-primary-600 hover:border-primary-600 hover:text-background-400 disabled:hover:bg-primary disabled:hover:text-white focus:bg-primary-700 focus:border-primary-700 focus:text-white focus-visible:shadow-focus disabled:opacity-35',
        mopar:
          'action2-bold uppercase p-0 text-primary hover:text-primary-400 hover:bg-transparent underline underline-offset-2',
        jeep: 'action2-bold uppercase p-0 text-primary hover:text-primary-400 hover:bg-transparent underline underline-offset-2',
        mini: 'body1-bold p-0 border-b border-fontPrimary rounded-none hover:text-primary hover:border-primary disabled:opacity-35 disabled:cursor-not-allowed disabled:hover:text-fontPrimary disabled:hover:border-fontPrimary focus:text-primary focus:border-primary',
        jaguar:
          'action2-bold bg-primary text-white uppercase p-4 border border-fontPrimary hover:bg-background hover:text-fontPrimary disabled:hover:bg-primary disabled:hover:text-white focus:bg-background focus:text-fontPrimary focus-visible:shadow-focus disabled:opacity-35',
        jlr: 'action2 text-primary border border-primary rounded-custom px-6 py-4 mt-3 uppercase hover:bg-primary hover:text-white focus-visible:bg-primary focus-visible:text-white',
        common: `action2 font-semibold normal-case rounded-custom p-0 mt-3 hover:underline ${componentStyles.value?.button?.ghost}`,
        referral:
          'action2 text-primary border border-primary rounded-custom px-6 py-4 mt-3 uppercase hover:bg-primary hover:text-white focus-visible:bg-primary focus-visible:text-white',
        landrover:
          'body1 bg-primary text-white uppercase p-4 border border-fontPrimary hover:bg-background hover:text-fontPrimary disabled:hover:bg-primary disabled:hover:text-white focus:bg-background focus:text-fontPrimary focus-visible:shadow-focus disabled:opacity-35',
        volvo:
          'action2-bold text-primary border border-primary rounded-custom px-6 py-3 mt-3 uppercase hover:bg-primary hover:text-white focus:bg-primary focus:text-white',
        crashdaddy:
          'action2-bold bg-primary text-white uppercase px-6 py-[18px] hover:bg-primary-400 hover:border-primary-400 border border-primary rounded-custom',
      },
    },
    defaultVariants: {
      variant: getClientTheme(),
    },
  });
</script>

<style scoped></style>
